import { useSelector } from 'react-redux';
import { metricSentenceCase, formatNumberGivenValueType } from 'modules/productAnalytics/utils';
import { StyledDivider } from 'modules/productAnalytics/Styled';
import { localize } from 'utils/numberHelpers';
import { Skeleton } from 'ui/skeleton';
import { AccordionBodyDeprecated, AccordionLabelDeprecated } from 'ui/accordion';
import { kebabCase } from 'utils/stringHelpers';
import { ARROW_DIRECTION_DOWN, ARROW_DIRECTION_UP } from 'ui/collapsibleContent';
import { MetricBreakdownCard } from './MetricBreakdownCard';
import {
  selectAnalyticsMetricsValueTypes,
  selectTopBounceReasonsBreakdownBuckets,
} from '../selectors';

import {
  MetricBreakdownContainer,
  MetricList,
  MetricRow,
  MetricBreakdownHeaderContainer,
  MetricBreakdownHeaderTitle,
  MetricBreakdownHeaderValue,
  SkeletonWrapper,
  MetricBreakdownRowWithTwoUnequalColumns,
  BreakdownAccordionWrapper,
  BreakdownAccordion,
  AccordionLabelRow,
  AccordionBodyRow,
  StyledReasons,
  StyledListContainer,
} from './Styled';
import { MetricDefinitionWrapper } from './MetricDefinitionWrapper';

export const BounceMetricCard = ({ metricValues, results, isLoading, headerChart }) => {
  const buckets = useSelector(selectTopBounceReasonsBreakdownBuckets) || {};
  const valueTypes = useSelector(selectAnalyticsMetricsValueTypes);

  const totalKey = metricValues.find((el) => el.includes('Total'));
  const uniqueKey = metricValues.find((el) => el.includes('Unique'));
  const rateKey = metricValues.find((el) => el.includes('Rate'));
  const { SOFT_BOUNCE, HARD_BOUNCE } = buckets;

  return (
    <MetricBreakdownCard title="Bounces">
      {isLoading ? (
        <SkeletonWrapper height={256} padding={24}>
          <Skeleton />
        </SkeletonWrapper>
      ) : (
        <MetricBreakdownContainer>
          <MetricBreakdownHeaderContainer>
            <MetricBreakdownRowWithTwoUnequalColumns>
              <div>
                <MetricBreakdownHeaderTitle data-test={`metric-title-${kebabCase(rateKey)}`}>
                  <MetricDefinitionWrapper metricName={rateKey}>
                    {metricSentenceCase(rateKey)}
                  </MetricDefinitionWrapper>
                </MetricBreakdownHeaderTitle>
                <MetricBreakdownHeaderValue data-test={`metric-value-${kebabCase(rateKey)}`}>
                  {formatNumberGivenValueType(valueTypes[rateKey], results[rateKey]?.value) ||
                    'N/A'}
                </MetricBreakdownHeaderValue>
              </div>
              {headerChart}
            </MetricBreakdownRowWithTwoUnequalColumns>
          </MetricBreakdownHeaderContainer>
          <StyledDivider />
          <StyledListContainer>
            <MetricList>
              <MetricRow>
                <span data-test={`metric-title-${kebabCase(uniqueKey)}`}>
                  <MetricDefinitionWrapper metricName={uniqueKey}>
                    {metricSentenceCase(uniqueKey)}
                  </MetricDefinitionWrapper>
                </span>
                <span data-test={`metric-value-${kebabCase(uniqueKey)}`}>
                  {formatNumberGivenValueType(valueTypes[uniqueKey], results[uniqueKey]?.value) ||
                    'N/A'}
                </span>
              </MetricRow>
            </MetricList>

            <MetricList>
              <MetricRow>
                <span data-test={`metric-title-${kebabCase(totalKey)}`}>
                  <MetricDefinitionWrapper metricName={totalKey}>
                    {metricSentenceCase(totalKey)}
                  </MetricDefinitionWrapper>
                </span>
                <span data-test={`metric-value-${kebabCase(totalKey)}`}>
                  {formatNumberGivenValueType(valueTypes[totalKey], results[totalKey]?.value) ||
                    'N/A'}
                </span>
              </MetricRow>
              {Object.keys(buckets).length > 0 && (
                <BreakdownAccordionWrapper>
                  {SOFT_BOUNCE && (
                    <BreakdownAccordion>
                      <AccordionLabelDeprecated
                        arrowHeight={16}
                        arrowWidth={16}
                        arrowStart={ARROW_DIRECTION_DOWN}
                        arrowEnd={ARROW_DIRECTION_UP}
                      >
                        <AccordionLabelRow>
                          <span>{SOFT_BOUNCE.count.toLocaleString()} soft bounces</span>
                          <StyledReasons>{SOFT_BOUNCE.bounceReasons?.length} reasons</StyledReasons>
                        </AccordionLabelRow>
                      </AccordionLabelDeprecated>
                      <AccordionBodyDeprecated>
                        {SOFT_BOUNCE.bounceReasons?.map((el) => (
                          <AccordionBodyRow key={el.reason}>
                            <span>{el.reason}</span>
                            <span>{localize(el?.count)}</span>
                          </AccordionBodyRow>
                        ))}
                      </AccordionBodyDeprecated>
                    </BreakdownAccordion>
                  )}
                  {HARD_BOUNCE && (
                    <BreakdownAccordion>
                      <AccordionLabelDeprecated
                        arrowHeight={16}
                        arrowWidth={16}
                        arrowStart={ARROW_DIRECTION_DOWN}
                        arrowEnd={ARROW_DIRECTION_UP}
                      >
                        <AccordionLabelRow>
                          <span>{HARD_BOUNCE.count.toLocaleString()} hard bounces</span>
                          <StyledReasons>{HARD_BOUNCE.bounceReasons?.length} reasons</StyledReasons>
                        </AccordionLabelRow>
                      </AccordionLabelDeprecated>
                      <AccordionBodyDeprecated>
                        {HARD_BOUNCE.bounceReasons?.map((el) => (
                          <AccordionBodyRow key={el.reason}>
                            <span>{el.reason}</span>
                            <span>{localize(el?.count)}</span>
                          </AccordionBodyRow>
                        ))}
                      </AccordionBodyDeprecated>
                    </BreakdownAccordion>
                  )}
                </BreakdownAccordionWrapper>
              )}
            </MetricList>
          </StyledListContainer>
        </MetricBreakdownContainer>
      )}
    </MetricBreakdownCard>
  );
};
